import {Inject, Injectable} from '@angular/core';
import {Params} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {RoutingService, User} from '@spartacus/core';
import {ConfiguratorRouter, ConfiguratorRouterExtractorService} from '@spartacus/product-configurator/common';
import {Configurator} from '@spartacus/product-configurator/rulebased';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {combineLatest, Observable, of} from 'rxjs';
import {distinctUntilChanged, filter, first, map, switchMap} from 'rxjs/operators';
import {DcpUiType, ExternalAttribute, FeatureToggleConfig} from '../models';
import {AttributeValueHandler} from '../models/configurator-attribute-value-handler';
import {ConfigurationActions} from '../store';
import token from '../token/tokens';
import * as ConfigUtils from '../utils';
import {
  BUSINESS_MODEL_ATTRIBUTE,
  deepEqual,
  EDITION_ATTRIBUTE,
  END_CUSTOMER_ID_ATTRIBUTE,
  END_CUSTOMER_NAME_ATTRIBUTE,
  GENERAL_GROUP_DESCRIPTION,
  OPPORTUNITY_ID_ATTRIBUTE,
  QUANTITY_ROOT_ATTRIBUTE,
  SKIP_CONFIGURATOR,
  START_DATE_ATTRIBUTE,
} from '../utils';
import {
  ALLOWED_TECHNICAL_ATTRIBUTES,
  END_DATE_ATTRIBUTE,
  END_DATE_STO_ATTRIBUTE,
  ENTITLEMENT_SET_ATTRIBUTE,
  HIDDEN_ATTRIBUTES,
  READ_ONLY_ATTRIBUTES,
} from './../utils';
import {DcpConfiguratorCommonsService} from './dcp-configurator-commons.service';
import {DcpLoggingService} from './dcp-logging.service';
import {FeatureTogglesService} from './feature-toggles.service';
import CustomUiTypeIndicator = Configurator.CustomUiTypeIndicator;
import UiType = Configurator.UiType;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DCPConfiguratorDeepLinkingService {
  attributesToHide = [END_DATE_ATTRIBUTE, END_DATE_STO_ATTRIBUTE, ENTITLEMENT_SET_ATTRIBUTE];
  attributesToSetReadOnly: string[] = [
    OPPORTUNITY_ID_ATTRIBUTE,
    END_CUSTOMER_ID_ATTRIBUTE,
    END_CUSTOMER_NAME_ATTRIBUTE,
    BUSINESS_MODEL_ATTRIBUTE,
  ];
  currentUser?: User;
  requiresConfigUpdate?: boolean;
  externalAttributeNamesToSkip = [
    SKIP_CONFIGURATOR,
    START_DATE_ATTRIBUTE,
    END_DATE_STO_ATTRIBUTE,
    QUANTITY_ROOT_ATTRIBUTE,
  ];

  constructor(
    private store: Store<{}>,
    protected routingService: RoutingService,
    protected configRouterExtractorService: ConfiguratorRouterExtractorService,
    protected configuratorCommonsService: DcpConfiguratorCommonsService,
    @Inject(token.services.ATTRIBUTE_VALUE_HANDLER)
    protected attributeHandlers: AttributeValueHandler[],
    @Inject(token.values.FEATURE_TOGGLES)
    protected featureToggles: FeatureToggleConfig,
    protected featureToggleService: FeatureTogglesService,
    protected loggingService: DcpLoggingService,
    protected userFacade: UserAccountFacade,
  ) {}

  static isEqualAdjustedConfig(config1: any, config2: any) {
    return deepEqual(
      {
        ...config1,
        priceSummary: undefined,
        interactionState: undefined,
        priceSupplements: [],
      },
      {
        ...config2,
        priceSummary: undefined,
        interactionState: undefined,
        priceSupplements: [],
      },
    );
  }

  public getExternalAttributes(): Observable<any> {
    return this.configRouterExtractorService.extractRouterData().pipe(
      first(),
      switchMap((routingData) => {
        routingData?.externalAttributes?.forEach((attribute, index, attributes) => {
          attributes[index].value = this.convertAttributeValue(attribute.value);
        });
        return this.pickExternalAttributesToUpdate(routingData);
      }),
      untilDestroyed(this),
    );
  }

  loadReadOnlyAttributes(configuration: Configurator.Configuration): Observable<string[]> {
    return this.loadAttributeValuesFromAllSources(
      ConfigUtils.READ_ONLY_ATTRIBUTES,
      configuration,
      this.attributesToSetReadOnly,
    );
  }

  loadHiddenAttributes(configuration: Configurator.Configuration): Observable<string[]> {
    return this.loadAttributeValuesFromAllSources(ConfigUtils.HIDDEN_ATTRIBUTES, configuration, this.attributesToHide);
  }

  pickExternalAttributesToUpdate(routingData: ConfiguratorRouter.Data): Observable<any[]> {
    let quantityFlag = false; //adding extra flag to enforce addition of quantity to newAttributes
    return this.getConfiguration().pipe(
      distinctUntilChanged(DCPConfiguratorDeepLinkingService.isEqualAdjustedConfig),
      map((configuration) => {
        const newAttributes: any[] = [];
        this.configuratorCommonsService.setInitialLoadingState(true);
        configuration.groups.forEach((group: Configurator.Group) => {
          if (group.description === GENERAL_GROUP_DESCRIPTION) {
            const editionSelected = !group.attributes?.find((attribute) => attribute.name === EDITION_ATTRIBUTE)
              ?.userInput;
            if (editionSelected) {
              quantityFlag = true;
            }
          }
          routingData?.externalAttributes?.forEach((externalAttribute) => {
            const attributeValue = externalAttribute.value;
            const attributeName = externalAttribute.name;
            const attribute = ConfigUtils.findAttributeInGroup(group, attributeName);
            if (attribute) {
              //if flagged then it means that LIC_EDITION was added to the newAttributes so we need to add the quantity too
              if (attributeName === QUANTITY_ROOT_ATTRIBUTE && quantityFlag === true) {
                if (attribute.userInput !== attributeValue) {
                  const selAttr = this.selectAttribute(attribute, attributeValue);
                  newAttributes.push(selAttr);
                  quantityFlag = false;
                }
              } else {
                this.requiresConfigUpdate =
                  !(attribute.userInput !== undefined && attribute?.userInput?.includes(attributeValue)) &&
                  !ConfigUtils.isMultiValueAttribute(attribute);
                const selectedAttribute = this.selectAttribute(attribute, attributeValue);
                if (this.requiresConfigUpdate) {
                  newAttributes.push(selectedAttribute);
                }
              }
            }
          });
        });
        if (newAttributes.length === 0) {
          this.configuratorCommonsService.setInitialLoadingState(false);
        }
        return newAttributes;
      }),
    );
  }

  private selectAttribute(attribute: Configurator.Attribute, attributeValue: any): Configurator.Attribute {
    const copiedAttribute = JSON.parse(JSON.stringify(attribute));
    copiedAttribute.userInput = attributeValue;
    copiedAttribute.selectedSingleValue = attributeValue;

    if (attribute?.values && !attribute.userInput && !attribute.selectedSingleValue) {
      const attributeValues = attributeValue.includes(',') ? attributeValue.split(',') : [attributeValue];
      attributeValues.forEach((value: any) => {
        const domainValue = copiedAttribute?.values?.find((v: {valueCode: any}) => v.valueCode === value);
        if (domainValue && !domainValue.selected) {
          domainValue.selected = true;
          this.requiresConfigUpdate = true;
        }
      });
    }
    return copiedAttribute;
  }

  public loadConfigurationWithMetaData(
    config: Observable<Configurator.Configuration>,
  ): Observable<Configurator.Configuration> {
    return config.pipe(
      filter((configuration) => configuration.groups?.length > 0),
      distinctUntilChanged(deepEqual),
      switchMap((config) =>
        combineLatest([
          of(config),
          this.loadHiddenAttributes(config).pipe(distinctUntilChanged()),
          this.loadReadOnlyAttributes(config).pipe(distinctUntilChanged()),
          this.featureToggleService.isEnabled(this.featureToggles.cpsConfiguratorDisplayTechnicalGroup),
        ]),
      ),
      map(([config, hiddenAttrs, readOnlyAttrs, showTechnicalGroup]) =>
        this.reflectMetaDataInConfigurationGroup(config, hiddenAttrs, readOnlyAttrs, showTechnicalGroup),
      ),
      distinctUntilChanged(deepEqual),
    );
  }

  removeTechnicalGroup(config: Configurator.Configuration): void {
    config.groups = config.groups.filter(ConfigUtils.isNotTechnicalGroup);
  }

  public reflectMetaDataInConfigurationGroup = (
    config: Configurator.Configuration,
    hiddenAttrs: string[],
    readOnlyAttrs: string[],
    showTechnicalGroup: boolean,
  ): Configurator.Configuration => {
    const clonedConfig = JSON.parse(JSON.stringify(config));
    clonedConfig.groups.forEach((group: Configurator.Group) => {
      this.setAttributeMetaDataInGroup(group, readOnlyAttrs, hiddenAttrs);
      group?.subGroups?.map((group) => this.setAttributeMetaDataInGroup(group, readOnlyAttrs, hiddenAttrs));
    });
    clonedConfig.flatGroups.forEach((group: Configurator.Group) =>
      this.setAttributeMetaDataInGroup(group, readOnlyAttrs, hiddenAttrs),
    );
    if (!showTechnicalGroup) {
      this.removeTechnicalGroup(clonedConfig);
    }
    return clonedConfig;
  };

  setAttributeMetaDataInGroup(group: Configurator.Group, readOnlyAttrs: string[], hiddenAttrs: string[]) {
    group?.attributes?.forEach((attribute) => {
      const READ_ONLY_QUANTITY_UI_TYPE = UiType.NUMERIC + CustomUiTypeIndicator + DcpUiType.READ_ONLY_QUANTITY;
      const READ_ONLY_ADDON_UI_TYPE = UiType.CHECKBOX + CustomUiTypeIndicator + DcpUiType.READ_ONLY_ADDON;
      const READ_ONLY_DATE_UI_TYPE = UiType.READ_ONLY + CustomUiTypeIndicator + DcpUiType.DATE;

      if (readOnlyAttrs?.includes(attribute?.name)) {
        if (
          attribute.uiTypeVariation === READ_ONLY_QUANTITY_UI_TYPE ||
          attribute.uiTypeVariation === READ_ONLY_ADDON_UI_TYPE ||
          attribute.uiTypeVariation === READ_ONLY_DATE_UI_TYPE
        ) {
          // Handled already in other group or iteration
          return;
        } else if (attribute.uiTypeVariation === UiType.NUMERIC + CustomUiTypeIndicator + DcpUiType.QUANTITY) {
          // Use quantity component to allow patching default meta data
          attribute.uiTypeVariation = READ_ONLY_QUANTITY_UI_TYPE;
        } else if (attribute.uiTypeVariation === UiType.CHECKBOX + CustomUiTypeIndicator + DcpUiType.ADDON) {
          attribute.uiTypeVariation = READ_ONLY_ADDON_UI_TYPE;
        } else if (attribute.uiTypeVariation === UiType.STRING + CustomUiTypeIndicator + DcpUiType.DATE) {
          attribute.uiTypeVariation = READ_ONLY_DATE_UI_TYPE;
        } else {
          attribute.uiTypeVariation = 'any' + CustomUiTypeIndicator + UiType.READ_ONLY;
        }
      }
      if (hiddenAttrs?.includes(attribute?.name) || attribute.hidden) {
        attribute.hidden = true;
        attribute.visible = false;
      }
    });
  }

  convertAttributeValue(attributeValue: string): string {
    const mapKey = attributeValue.toUpperCase();
    const filteredAttributeHandler = this.attributeHandlers.filter((e) => e.key === mapKey);
    if (filteredAttributeHandler.length > 0) {
      return filteredAttributeHandler[0].handle(mapKey);
    }
    return attributeValue;
  }

  private getCancellationUrl(params: Params): string {
    const baseUrl = this.routingService.getFullUrl([]);
    let urlParams = '';
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        urlParams += `${key}=${params[key]}&`;
      }
    }
    // Remove the last '&' character
    urlParams = urlParams.slice(0, -1);
    return `${baseUrl}?${urlParams}`;
  }

  public generateBookmarkParams() {
    return this.getConfiguration().pipe(
      map((configuration) => {
        const bookMarkQueryParams: Params = {};
        configuration.groups.forEach((group) => {
          if (ConfigUtils.isTechnicalGroup(group)) {
            const technicalAttributes = group.attributes?.filter(
              (a) =>
                a.name === READ_ONLY_ATTRIBUTES ||
                a.name === HIDDEN_ATTRIBUTES ||
                ALLOWED_TECHNICAL_ATTRIBUTES.includes(a.name),
            );
            this.loadBookMarkParamsForAttributes(technicalAttributes ?? [], bookMarkQueryParams);
          } else {
            this.loadBookMarkParamsForAttributes(group.attributes ?? [], bookMarkQueryParams);
          }
        });
        bookMarkQueryParams['reset'] = 'true';
        const cancellationUrl = this.getCancellationUrl(bookMarkQueryParams);
        this.store.dispatch(
          ConfigurationActions.updateConfiguration({
            cancellationUrl: cancellationUrl,
          }),
        );
        return bookMarkQueryParams;
      }),
    );
  }

  logIncorrectDeeplinkParameters(configuration: Configurator.Configuration, externalAttributes?: ExternalAttribute[]) {
    if (!externalAttributes) {
      return;
    }

    const allAttributes = configuration.flatGroups.flatMap((g) => g.attributes);

    const mismatchedKeys: string[] = this.findMismatchKeys(allAttributes, externalAttributes);
    const mismatchedKeyValuePairs = this.findMismatchKeyValuePairs(allAttributes, externalAttributes);

    if (!mismatchedKeys.length && !mismatchedKeyValuePairs.size) {
      // do nothing when there are no mismatches
      return;
    }

    this.userFacade
      .get()
      .pipe(untilDestroyed(this))
      .subscribe((user?: User) => {
        this.currentUser = user;

        let errorMsg = 'Mismatched parameters found in the deeplink URL:';
        if (mismatchedKeys.length) {
          errorMsg = errorMsg.concat(` mismatchedKeys: ${mismatchedKeys}`);
        }
        if (mismatchedKeyValuePairs.size) {
          errorMsg = errorMsg.concat(` mismatchedKeyValuePairs: ${[...mismatchedKeyValuePairs.entries()]}`);
        }

        this.loggingService.error(
          errorMsg.concat(` for the product: ${configuration.productCode} and the user: ${this.currentUser?.uid}`),
          this.constructor.name,
        );
      });
  }

  private findMismatchKeys(
    allAttributes: (Configurator.Attribute | undefined)[],
    externalAttributes: ExternalAttribute[],
  ): string[] {
    const mismatchedKeys: string[] = [];
    externalAttributes.forEach((externalAttribute: ExternalAttribute) => {
      if (this.externalAttributeNamesToSkip.includes(externalAttribute.name)) {
        return;
      }
      if (
        !allAttributes
          .map((a) => a?.name)
          .flat()
          .includes(externalAttribute.name)
      ) {
        mismatchedKeys.push(externalAttribute.name);
      }
    });
    return mismatchedKeys;
  }

  private findMismatchKeyValuePairs(
    allAttributes: (Configurator.Attribute | undefined)[],
    externalAttributes: ExternalAttribute[],
  ) {
    const mismatchedKeyValuePairs = new Map();
    externalAttributes.forEach((externalAttribute) => {
      if (this.externalAttributeNamesToSkip.includes(externalAttribute.name)) {
        return;
      }
      const values = allAttributes
        .find((attribute) => attribute?.name === externalAttribute.name)
        ?.values?.filter((v) => v.selected)
        .map((v) => v.valueCode.toLowerCase());
      if (!(values == null) && values.length > 0) {
        const externalAttributeValues = externalAttribute.value.toLowerCase().split(',');
        if (!values.every((el: string) => externalAttributeValues.includes(el))) {
          // values dont contains all externalAttributeValues
          mismatchedKeyValuePairs.set(externalAttribute.name, externalAttribute.value);
        }
      }
    });
    return mismatchedKeyValuePairs;
  }

  private loadAttributeValuesFromAllSources(
    attributeName: string,
    configuration: Configurator.Configuration,
    staticConfig: string[],
  ): Observable<string[]> {
    return combineLatest([
      this.configRouterExtractorService.extractRouterData().pipe(first(), untilDestroyed(this)),
      of(configuration),
    ]).pipe(
      map(([routerData, configuration]) => {
        const configuratorValue = configuration?.flatGroups
          .flatMap((g) => g.attributes)
          ?.find((attribute) => attribute?.name?.includes(attributeName))
          ?.values?.filter((v) => v.selected)
          .map((v) => v.valueCode);
        const deepLinkValue = routerData?.externalAttributes
          ?.find((attribute) => attribute?.name.includes(attributeName))
          ?.value?.split(',');
        const result = ([] as string[])
          .concat(configuratorValue ?? [])
          .concat(deepLinkValue ?? [])
          .concat(staticConfig ?? []);
        return [...new Set(result)];
      }),
    );
  }

  private loadBookMarkParamsForAttributes(attributes: Configurator.Attribute[], bookMarkQueryParams: Params) {
    attributes
      .filter(
        (attribute) => attribute.userInput || attribute.selectedSingleValue || (attribute.values?.length ?? 0) > 0,
      )
      .forEach((attribute) => {
        let value;
        if (attribute.values && attribute.values.length > 0) {
          value = attribute.values
            .filter((v) => v.selected || ALLOWED_TECHNICAL_ATTRIBUTES.includes(v.name ?? ''))
            .map((v) => v.valueCode)
            .join(',');
        }
        if (!value) {
          value = attribute.userInput ? attribute.userInput : attribute.selectedSingleValue;
        }
        bookMarkQueryParams[attribute.name] = value;
      });
  }

  private getConfiguration(): Observable<Configurator.Configuration> {
    return this.configRouterExtractorService.extractRouterData().pipe(
      filter((routerData) => routerData.pageType === ConfiguratorRouter.PageType.CONFIGURATION),
      switchMap((routerData) => this.configuratorCommonsService.getConfiguration(routerData.owner)),
    );
  }
}
